import {
  Box,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Provider,
  RestoreGcpCloudSqlInputNetworkTypeEnum,
} from '@repo/api-gw-sdk';
import { useState } from 'react';

import { AvailabilityZoneSelect } from '@/components/regions/availabilityZoneSelect';
import { GcpSubnetSelect } from '@/components/regions/gcpSubnetSelect';
import { RegionSelect } from '@/components/regions/regionSelect';
import { TransactionalTextField } from '@/components/shared/transactionalTextField';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { StepContainer } from '../../../wizard/StepContainer';
import type { RestoreGcpCloudSqlState } from '../../restoreGcpCloudSqlWizard';
import { EonTagRemark } from '../eonTagRemark';

export const GcpCloudSqlConfiguration = (
  props: StepProps<RestoreGcpCloudSqlState>
) => {
  const [error, setError] = useState<string | undefined>();
  const [isRestoring, setIsRestoring] = useState(false);
  const [networkType] = useState(
    RestoreGcpCloudSqlInputNetworkTypeEnum.Private
  );
  const dal = useDAL();

  // const isPublicNetworkType =
  //   networkType === RestoreGcpCloudSqlInputNetworkTypeEnum.Public;
  const isPrivateNetworkType =
    networkType === RestoreGcpCloudSqlInputNetworkTypeEnum.Private;

  const onNextClick = () => {
    setIsRestoring(true);
    setError(undefined);

    const labels = {
      ...(props.wizardState.keepOriginalTags
        ? props.wizardState.initialTags
        : props.wizardState.tags),
      'eon-restore': 'true',
    };

    return dal.restore.gcpCloudSql
      .restore(props.wizardState.resourceId, props.wizardState.snapshot.id, {
        restoreAccountId: props.wizardState.restoreAccount!.id,
        zone: props.wizardState.zone!,
        name: props.wizardState.resourceName ?? '',
        networkInterface: isPrivateNetworkType
          ? props.wizardState.network
          : undefined,
        networkType,
        labels,
      })
      .then(() => props.finish('Restore job has started!'))
      .catch((e) => {
        setIsRestoring(false);
        setError(`Failed to restore DB: ${e.message}`);
      });
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      stepperLabels={props.stepperLabels}
      stepperIndex={props.stepperLabels.length - 1}
      nextButtonText='Restore'
      onBackClick={props.back}
      canGoNext={() => {
        if (
          isRestoring ||
          !props.wizardState.resourceName ||
          !props.wizardState.regionName
        ) {
          return false;
        }

        if (isPrivateNetworkType) {
          return !!props.wizardState.network;
        }

        return true;
      }}
      navigationComponent={
        error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <EonTagRemark provider={Provider.Gcp} />
        )
      }
      onNextClick={onNextClick}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
        />
      }
    >
      <Box className='mx-[40px] my-[24px]'>
        <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
          <FormControl size='small' className='flex-1'>
            <FormLabel>Instance Id*</FormLabel>
            <TransactionalTextField
              initValue={props.wizardState.resourceName}
              onChange={(resourceName) =>
                props.setWizardState((state) => ({ ...state, resourceName }))
              }
            />
          </FormControl>
        </Stack>
        <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
          <RegionSelect
            cloudProvider={Provider.Gcp}
            accountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            onChange={(regionName) =>
              props.setWizardState((state) => ({
                ...state,
                zone: undefined,
                regionName,
              }))
            }
          />
          <AvailabilityZoneSelect
            accountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            availabilityZone={props.wizardState.zone}
            cloudProvider={Provider.Gcp}
            onChange={(zone) =>
              props.setWizardState((state) => ({
                ...state,
                zone,
              }))
            }
          />
        </Stack>
        <Stack className='mt-[20px] mb-[24px]'>
          {/* <FormLabel>Instance IP assignment</FormLabel> */}
          <Stack direction='row' alignItems='center' gap='8px'>
            <GcpSubnetSelect
              accountId={props.wizardState.restoreAccount?.id}
              region={props.wizardState.regionName}
              subnet={props.wizardState.network}
              onChange={(network) =>
                props.setWizardState((state) => ({
                  ...state,
                  network,
                }))
              }
            />
            <Tooltip
              className='mt-[30px]'
              title={
                'When using a private IP, only peered networks are available.'
              }
              slotProps={{
                tooltip: {
                  sx: {
                    padding: '16px',
                  },
                },
              }}
            >
              <i className=' material-symbols-info-outline h-[20px] w-[20px] p-[2px]' />
            </Tooltip>
          </Stack>
          {/* <List className='w-full'>
            <OptionListItem
              data-testid='publicNetworkType'
              isChecked={isPublicNetworkType}
              onClick={() => {
                setNetworkType(RestoreGcpCloudSqlInputNetworkTypeEnum.Public);
              }}
            >
              <FormControlLabel
                checked={isPublicNetworkType}
                onClick={() => {
                  setNetworkType(RestoreGcpCloudSqlInputNetworkTypeEnum.Public);
                }}
                control={<Radio />}
                label='Public IP'
              />
            </OptionListItem>
            <OptionListItem
              data-testid='privateNetworkType'
              isChecked={isPrivateNetworkType}
              onClick={() =>
                setNetworkType(RestoreGcpCloudSqlInputNetworkTypeEnum.Private)
              }
            >
              <FormControlLabel
                checked={isPrivateNetworkType}
                onClick={() =>
                  setNetworkType(RestoreGcpCloudSqlInputNetworkTypeEnum.Private)
                }
                control={<Radio />}
                label='Private IP'
              />
              <Collapse className='w-full' in={isPrivateNetworkType}>
                <Stack className='w-1/2 mt-[24px]'>
                  <Stack direction='row' alignItems='center' gap='8px'>
                    <GcpSubnetSelect
                      allowOnlyPeeredNetworks
                      accountId={props.wizardState.restoreAccount?.id}
                      region={props.wizardState.regionName}
                      subnet={props.wizardState.network}
                      onChange={(network) =>
                        props.setWizardState((state) => ({
                          ...state,
                          network,
                        }))
                      }
                    />
                    <Tooltip
                      className='mt-[30px]'
                      title={
                        'When using a private IP, only peered networks are available.'
                      }
                      slotProps={{
                        tooltip: {
                          sx: {
                            padding: '16px',
                          },
                        },
                      }}
                    >
                      <i className=' material-symbols-info-outline h-[20px] w-[20px] p-[2px]' />
                    </Tooltip>
                  </Stack>
                </Stack>
              </Collapse>
            </OptionListItem>
          </List> */}
        </Stack>
      </Box>
      <Divider />
      <CardContent className='px-0'>
        <TagsSection
          initialTags={props.wizardState.initialTags ?? {}}
          tags={props.wizardState.tags ?? {}}
          keepOriginalTags={props.wizardState.keepOriginalTags}
          setKeepOriginalTags={(keepOriginalTags) =>
            props.setWizardState((state) => ({ ...state, keepOriginalTags }))
          }
          onChange={(tags) =>
            props.setWizardState((state) => ({ ...state, tags }))
          }
          provider={Provider.Gcp}
        />
      </CardContent>
    </StepContainer>
  );
};
